import { z } from 'zod';
import { SubscriptionSummarySchema } from './billing.js';

// ─────────────────────────────────────────────────────────────────
// Workspaces & membership — shared with the editor UI
//
// Workspaces are the billing/credit boundary: credit balances,
// transactions, invoices and (eventually) subscriptions hang off a
// workspace. A user may belong to several workspaces and switches the
// active one in the UI; the active workspace is sent on requests as the
// `X-Workspace-Id` header.
// ─────────────────────────────────────────────────────────────────

export const WorkspaceRoleSchema = z.enum(['owner', 'admin', 'member']);
export type WorkspaceRole = z.infer<typeof WorkspaceRoleSchema>;

export const ROLE_RANK: Record<WorkspaceRole, number> = {
  owner: 3,
  admin: 2,
  member: 1,
};

export const WorkspaceNameSchema = z.string().trim().min(1).max(80);

export const CreateWorkspaceSchema = z.object({
  name: WorkspaceNameSchema,
});
export type CreateWorkspaceInput = z.infer<typeof CreateWorkspaceSchema>;

export const RenameWorkspaceSchema = CreateWorkspaceSchema;
export type RenameWorkspaceInput = z.infer<typeof RenameWorkspaceSchema>;

export const InviteMemberSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  role: WorkspaceRoleSchema.optional(), // defaults to 'member' server-side
});
export type InviteMemberInput = z.infer<typeof InviteMemberSchema>;

export const UpdateMemberRoleSchema = z.object({
  role: WorkspaceRoleSchema,
});
export type UpdateMemberRoleInput = z.infer<typeof UpdateMemberRoleSchema>;

export const AcceptInviteSchema = z.object({
  token: z.string().min(1),
});
export type AcceptInviteInput = z.infer<typeof AcceptInviteSchema>;

// ── Response shapes ──────────────────────────────────────────────

export const WorkspaceSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
  /** Caller's role inside this workspace. */
  role: WorkspaceRoleSchema,
  createdAt: z.string(),
  /**
   * True while the workspace has never finished (or dismissed) its onboarding
   * wizard — see `GET /api/workspaces/:id/setup`. Drives the one-time
   * redirect into `/workspace/setup`, so it is part of the summary rather
   * than a second round-trip on every page.
   */
  needsSetup: z.boolean(),
});
export type WorkspaceSummary = z.infer<typeof WorkspaceSummarySchema>;

export const WorkspacesResultSchema = z.object({
  workspaces: z.array(WorkspaceSummarySchema),
});
export type WorkspacesResult = z.infer<typeof WorkspacesResultSchema>;

export const WorkspaceMemberInfoSchema = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: WorkspaceRoleSchema,
  createdAt: z.string(),
});
export type WorkspaceMemberInfo = z.infer<typeof WorkspaceMemberInfoSchema>;

export const WorkspaceMembersResultSchema = z.object({
  members: z.array(WorkspaceMemberInfoSchema),
});
export type WorkspaceMembersResult = z.infer<typeof WorkspaceMembersResultSchema>;

export const WorkspaceInviteInfoSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  email: z.string(),
  role: WorkspaceRoleSchema,
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
});
export type WorkspaceInviteInfo = z.infer<typeof WorkspaceInviteInfoSchema>;

export const WorkspaceInviteResultSchema = z.object({
  invite: WorkspaceInviteInfoSchema,
  /** Magic-link URL the inviter can share with the invitee. */
  acceptUrl: z.string(),
});
export type WorkspaceInviteResult = z.infer<typeof WorkspaceInviteResultSchema>;

export const WorkspaceInvitesResultSchema = z.object({
  /** Outstanding invites, each carrying its copyable accept link. */
  invites: z.array(
    WorkspaceInviteInfoSchema.extend({
      acceptUrl: z.string(),
    }),
  ),
});
export type WorkspaceInviteListItem = z.infer<
  typeof WorkspaceInvitesResultSchema
>['invites'][number];
export type WorkspaceInvitesResult = z.infer<typeof WorkspaceInvitesResultSchema>;

/** Public peek returned by GET /api/workspaces/invites/:token (pre-auth). */
export const WorkspaceInvitePeekSchema = z.object({
  workspaceName: z.string(),
  role: WorkspaceRoleSchema,
  email: z.string(),
});
export type WorkspaceInvitePeek = z.infer<typeof WorkspaceInvitePeekSchema>;

/** Result of redeeming an invite (POST /api/workspaces/invites/accept). */
export const WorkspaceInviteAcceptResultSchema = z.object({
  workspace: WorkspaceSummarySchema.optional(),
});
export type WorkspaceInviteAcceptResult = z.infer<typeof WorkspaceInviteAcceptResultSchema>;

// ─────────────────────────────────────────────────────────────────
// Workspace onboarding
//
// A new workspace is provisioned bare (name, owner membership, empty site
// settings) and its owner is walked through `/workspace/setup` on the first
// visit — the missing half of `POST /api/workspaces`, which used to leave a
// workspace with a name and nothing else. `GET /api/workspaces/:id/setup`
// returns everything the wizard needs in one request so each step can be
// prefilled and re-entered; the steps themselves are a UI concern and are
// saved through the endpoints that already own that data (site settings,
// theme, invites, billing), never through a parallel onboarding store.
// ─────────────────────────────────────────────────────────────────

/**
 * A workspace needs onboarding while it has neither finished nor dismissed
 * the wizard. Both timestamps therefore mean "stop sending the owner here",
 * and a workspace that was never touched keeps neither.
 *
 * Lives next to the schemas because it is a rule about two of their fields,
 * not about how they are stored — the server decides the flag with it and the
 * editor only ever reads the result.
 */
export function needsSetup(workspace: {
  setupCompletedAt: string | null;
  setupSkippedAt: string | null;
}): boolean {
  return !workspace.setupCompletedAt && !workspace.setupSkippedAt;
}

export const WorkspaceSetupThemeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Bundled themes are managed by the deployment and cannot be uninstalled. */
  isBundled: z.boolean(),
});
export type WorkspaceSetupTheme = z.infer<typeof WorkspaceSetupThemeSchema>;

export const WorkspaceSetupSiteSchema = z.object({
  siteName: z.string(),
  siteDescription: z.string().nullable(),
  baseUrl: z.string(),
  activeThemeId: z.string(),
});
export type WorkspaceSetupSite = z.infer<typeof WorkspaceSetupSiteSchema>;

export const WorkspaceSetupStateSchema = z.object({
  workspace: z.object({
    id: z.string().uuid(),
    name: z.string(),
    role: WorkspaceRoleSchema,
    needsSetup: z.boolean(),
    completedAt: z.string().nullable(),
    skippedAt: z.string().nullable(),
  }),
  /** Null only when the site settings row could not be provisioned (no theme installed). */
  site: WorkspaceSetupSiteSchema.nullable(),
  themes: z.array(WorkspaceSetupThemeSchema),
  team: z.object({
    memberCount: z.number().int(),
    pendingInviteCount: z.number().int(),
  }),
  /** Live subscription, when the workspace already has one. */
  subscription: SubscriptionSummarySchema.nullable(),
});
export type WorkspaceSetupState = z.infer<typeof WorkspaceSetupStateSchema>;

/** Complete or dismiss onboarding. Both are idempotent. */
export const WorkspaceSetupActionSchema = z.enum(['complete', 'skip']);
export type WorkspaceSetupAction = z.infer<typeof WorkspaceSetupActionSchema>;

export const WorkspaceSetupResultSchema = z.object({
  workspaceId: z.string().uuid(),
  completedAt: z.string().nullable(),
  skippedAt: z.string().nullable(),
  needsSetup: z.boolean(),
});
export type WorkspaceSetupResult = z.infer<typeof WorkspaceSetupResultSchema>;
